import { Plus, X } from "lucide-react";
import type { CatalogFieldDef } from "./catalog-step-config-utils";
import { FieldShell } from "./catalog-step-domain-primitives";
import { stringifyConfigValue } from "./structured-config-utils";

interface ValueFieldProps {
  field: CatalogFieldDef;
  value: unknown;
  setValue: (key: string, value: unknown) => void;
}

export function MultiEnumField({ field, value, setValue }: ValueFieldProps) {
  const selected = new Set(toStringArray(value));
  return (
    <FieldShell label={field.label}>
      <div className="flex flex-wrap gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] p-2">
        {(field.schema.items?.enum ?? []).map((option) => {
          const checked = selected.has(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={checked}
              onClick={() => {
                const next = checked
                  ? [...selected].filter((item) => item !== option)
                  : [...selected, option];
                setValue(field.key, next);
              }}
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                checked
                  ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                  : "bg-[var(--muted)] text-[var(--muted-foreground)]"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </FieldShell>
  );
}

export function KeyValueObjectField({ field, value, setValue }: ValueFieldProps) {
  const rows = Object.entries(isRecord(value) ? value : {});
  const update = (nextRows: Array<[string, unknown]>) => {
    setValue(field.key, Object.fromEntries(nextRows.filter(([key]) => key.trim() !== "")));
  };

  return (
    <FieldShell label={field.label}>
      <div className="space-y-2 rounded-lg border border-[var(--border)] bg-[var(--card)] p-2">
        {rows.map(([key, val], index) => (
          <div key={`${key}-${index}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
            <input
              aria-label={`${field.label} key`}
              value={key}
              onChange={(event) => {
                const next = [...rows];
                next[index] = [event.target.value, val];
                update(next);
              }}
              className="h-8 rounded-md border border-[var(--border)] bg-transparent px-2 text-xs"
            />
            <input
              aria-label={`${field.label} value`}
              value={String(val ?? "")}
              onChange={(event) => {
                const next = [...rows];
                next[index] = [key, event.target.value];
                update(next);
              }}
              className="h-8 rounded-md border border-[var(--border)] bg-transparent px-2 text-xs"
            />
            <button
              type="button"
              onClick={() => update(rows.filter((_, rowIndex) => rowIndex !== index))}
              className="flex size-8 items-center justify-center rounded-md text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
              aria-label={`Remove ${key}`}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => update([...rows, ["", ""]])}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-[var(--border)] px-2 text-xs"
        >
          <Plus className="size-3.5" />
          Add
        </button>
      </div>
    </FieldShell>
  );
}

export function TextConfigField({ field, value, setValue }: ValueFieldProps) {
  return (
    <FieldShell label={field.label}>
      <input
        aria-label={field.label}
        type="text"
        value={typeof value === "string" ? value : stringifyConfigValue(value)}
        onChange={(event) => setValue(field.key, event.target.value)}
        className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
      />
    </FieldShell>
  );
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
